/**
 * IdGenTab — 시퀀스/식별자 생성 (C023) 라이브 데모
 * POST /api/eraf/platform-lab/ids
 * GET  /api/eraf/platform-lab/ids/batch
 * 5상태: default / loading / empty / error / (dialog — toast 복사 확인)
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EmptyState, ErrorState } from '@ui-kit';
import { useGenerateIds, useIdBatch } from '../hooks/usePlatformLab';
import { idGenSchema, idBatchSchema } from '../schema/platformLab.schema';
import type { IdGenFormValues, IdBatchFormValues } from '../schema/platformLab.schema';
import type { IdGenResponse, IdBatchResponse, IdKind, ApiError } from '../types/platformLab';

const ID_KINDS: IdKind[] = ['UUID', 'UUID_V7', 'ULID', 'SHORT', 'SNOWFLAKE', 'ALL'];

function IdRow({ label, value, onCopy }: { label: string; value?: string | number | null; onCopy: (v: string) => void }) {
  if (value == null) return null;
  const str = String(value);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', padding: 'var(--spacing-2) 0', borderBottom: '1px solid var(--color-surface-border)' }}>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', minWidth: 130, fontWeight: 600 }}>{label}</span>
      <code
        style={{
          flex: 1,
          fontFamily: 'var(--font-family-mono)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-primary)',
          background: 'var(--color-surface-muted)',
          padding: 'var(--spacing-1) var(--spacing-2)',
          borderRadius: 'var(--radius-sm)',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
        }}
      >
        {str}
      </code>
      <button
        type="button"
        onClick={() => onCopy(str)}
        aria-label={`복사: ${label}`}
        style={{
          padding: 'var(--spacing-1) var(--spacing-2)',
          background: 'transparent',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        복사
      </button>
    </div>
  );
}

export function IdGenTab() {
  const { t } = useTranslation();
  const generateMutation = useGenerateIds();
  const batchMutation = useIdBatch();

  const [idResult, setIdResult] = useState<IdGenResponse | null>(null);
  const [batchResult, setBatchResult] = useState<IdBatchResponse | null>(null);
  const [idError, setIdError] = useState<ApiError | null>(null);
  const [batchError, setBatchError] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const genForm = useForm<IdGenFormValues>({
    resolver: zodResolver(idGenSchema),
    defaultValues: { kind: 'ALL', length: 16 },
  });

  const batchForm = useForm<IdBatchFormValues>({
    resolver: zodResolver(idBatchSchema),
    defaultValues: { kind: 'SNOWFLAKE', count: 100 },
  });

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      showToast(t('platformLab.idgen.toast.copied'));
    });
  };

  const onGenerate = genForm.handleSubmit((values) => {
    setIdError(null);
    generateMutation.mutate({ kind: values.kind, length: values.length }, {
      onSuccess: (data) => setIdResult(data as IdGenResponse),
      onError: (err) => setIdError(err as unknown as ApiError),
    });
  });

  const onBatch = batchForm.handleSubmit((values) => {
    setBatchError(null);
    batchMutation.mutate({ kind: values.kind, count: values.count }, {
      onSuccess: (data) => setBatchResult(data as IdBatchResponse),
      onError: (err) => setBatchError(err as unknown as ApiError),
    });
  });


  return (
    <div>
      <h2 style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, marginBottom: 'var(--spacing-4)', color: 'var(--color-text-primary)' }}>
        {t('platformLab.idgen.title')}
      </h2>

      {/* ID 생성 폼 */}
      <form
        onSubmit={(e) => e.preventDefault()}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)', alignItems: 'flex-end', marginBottom: 'var(--spacing-4)' }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {t('platformLab.idgen.input.kind')}
          <select
            {...genForm.register('kind')}
            aria-label={t('platformLab.idgen.input.kind')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
            }}
          >
            {ID_KINDS.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {t('platformLab.idgen.input.length')}
          <input
            {...genForm.register('length')}
            type="number"
            min={4}
            max={64}
            aria-label={t('platformLab.idgen.input.length')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              width: 80,
              fontFamily: 'var(--font-family-sans)',
            }}
          />
        </label>

        <button
          type="button"
          onClick={onGenerate}
          disabled={generateMutation.isPending}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: generateMutation.isPending ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-family-sans)',
            opacity: generateMutation.isPending ? 0.6 : 1,
          }}
        >
          {generateMutation.isPending ? '...' : t('platformLab.idgen.button.generate')}
        </button>
      </form>

      {/* loading */}
      {generateMutation.isPending && (
        <div aria-busy="true" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--color-brand-primary)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
          생성 중...
        </div>
      )}

      {/* error */}
      {idError && !generateMutation.isPending && (
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <ErrorState
            title={t(`platformLab.error.${idError.code}`, { defaultValue: t('platformLab.error.network') })}
            message={idError.message}
            onRetry={() => setIdError(null)}
          />
        </div>
      )}

      {/* empty */}
      {!idResult && !idError && !generateMutation.isPending && (
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <EmptyState title={t('platformLab.idgen.empty.title')} />
        </div>
      )}

      {/* default — ID 결과 */}
      {idResult && !idError && !generateMutation.isPending && (
        <div style={{ marginBottom: 'var(--spacing-6)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-4)', background: 'var(--color-surface-background)' }}>
          <IdRow label={t('platformLab.idgen.label.uuid')} value={idResult.uuid} onCopy={handleCopy} />
          <IdRow label={t('platformLab.idgen.label.uuidV7')} value={idResult.uuidV7} onCopy={handleCopy} />
          <IdRow label={t('platformLab.idgen.label.ulid')} value={idResult.ulid} onCopy={handleCopy} />
          <IdRow label={t('platformLab.idgen.label.shortId')} value={idResult.shortId} onCopy={handleCopy} />
          <IdRow label={t('platformLab.idgen.label.snowflake')} value={idResult.snowflake} onCopy={handleCopy} />
          <p style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            generatedAt: {idResult.generatedAt}
          </p>
        </div>
      )}

      <hr style={{ border: 'none', borderTop: '1px solid var(--color-surface-border)', margin: 'var(--spacing-4) 0' }} />

      {/* Snowflake 배치 폼 */}
      <form
        onSubmit={(e) => e.preventDefault()}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)', alignItems: 'flex-end', marginBottom: 'var(--spacing-4)' }}
      >
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', alignSelf: 'center' }}>Snowflake 배치:</span>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {t('platformLab.idgen.input.count')}
          <input
            {...batchForm.register('count')}
            type="number"
            min={1}
            max={1000}
            aria-label={t('platformLab.idgen.input.count')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              width: 80,
              fontFamily: 'var(--font-family-sans)',
            }}
          />
        </label>

        <button
          type="button"
          onClick={onBatch}
          disabled={batchMutation.isPending}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: 'var(--color-surface-elevated)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: batchMutation.isPending ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-family-sans)',
            opacity: batchMutation.isPending ? 0.6 : 1,
          }}
        >
          {batchMutation.isPending ? '...' : t('platformLab.idgen.button.batch')}
        </button>
      </form>

      {/* batch loading */}
      {batchMutation.isPending && (
        <div aria-busy="true" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--color-brand-primary)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
          배치 생성 중...
        </div>
      )}

      {/* batch error */}
      {batchError && !batchMutation.isPending && (
        <ErrorState
          title={t(`platformLab.error.${batchError.code}`, { defaultValue: t('platformLab.error.network') })}
          message={batchError.message}
          onRetry={() => setBatchError(null)}
        />
      )}

      {/* batch 결과 */}
      {batchResult && !batchError && !batchMutation.isPending && (
        <div style={{ border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-4)', background: 'var(--color-surface-background)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-3)' }}>
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {batchResult.kind} × {batchResult.count}
            </span>
            {batchResult.monotonic && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-1)',
                  padding: 'var(--spacing-1) var(--spacing-2)',
                  background: 'var(--color-success-subtle, #e6f9f0)',
                  color: 'var(--color-success, #28a745)',
                  border: '1px solid var(--color-success, #28a745)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                }}
              >
                ✓ {t('platformLab.idgen.badge.monotonic')}
              </span>
            )}
          </div>
          <div
            style={{
              maxHeight: 200,
              overflowY: 'auto',
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
              background: 'var(--color-surface-muted)',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--spacing-3)',
            }}
          >
            {batchResult.ids.slice(0, 20).map((id, i) => (
              <div key={i}>{id}</div>
            ))}
            {batchResult.ids.length > 20 && (
              <div style={{ color: 'var(--color-text-muted)' }}>... {batchResult.ids.length - 20}개 더</div>
            )}
          </div>
        </div>
      )}

      {/* 토스트 — dialog 상태 (복사 확인) */}
      {toast && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {toast}
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
